import React, { useRef, useEffect, ReactElement, RefObject } from 'react';
import styles from './index.scss';

type Cell = {
	aspectRatio: string;
	height: number;
	isDefaultCell: boolean;
	tile: ReactElement;
	width: number;
	x: number;
	y: number;
};

type LayoutTilesViewProps = {
	activeSlide: number;
	children: ReactElement[];
	fading: boolean;
	fadingActive: string;
	isAllItemsVisible?: boolean;
	layout: number[][][];
	slideClsName: string;
	slideLength: number;
	slideRef: RefObject<HTMLDivElement> | undefined;
};

type TilePlacement = {
	width: number;
	height: number;
	x: number;
	y: number;
};

const LayoutTilesView = (props: LayoutTilesViewProps) => {
	const {
		layout,
		children,
		slideLength,
		slideRef,
		activeSlide,
		slideClsName,
		fading,
		fadingActive,
		isAllItemsVisible
	} = props;
	const gridContainer = useRef<HTMLInputElement>(null);
	const currTileIdx = useRef(0);

	const getTilePlacement = (tileLayout: number[][]): TilePlacement => {
		const yCoordinates: number[] = [];
		const xCoordinates: number[] = [];
		tileLayout.forEach(([x, y]: number[]): void => {
			xCoordinates.push(x);
			yCoordinates.push(y);
		});
		const xStart = Math.min(...xCoordinates);
		const xEnd = Math.max(...xCoordinates);
		const yStart = Math.min(...yCoordinates);
		const yEnd = Math.max(...yCoordinates);

		return { width: xEnd - xStart + 1, height: yEnd - yStart + 1, x: xStart - 1, y: yStart - 1 };
	};

	useEffect(() => {
		currTileIdx.current = 0;
	});

	const generateCells = (slideTiles: ReactElement[]): Cell[] => {
		return slideTiles.map((tile: ReactElement) => {
			let currTileLayout: number[][] = layout[currTileIdx.current];
			let isDefaultCell = false;
			if (!currTileLayout) {
				currTileLayout = [[1, 1]];
				isDefaultCell = true;
			}

			const { height, width, x, y } = getTilePlacement(currTileLayout);
			let aspectRatio = '1';
			if (height === 2 && width === 1) {
				aspectRatio = '0.5';
			} else if (width === 2 && height === 1) {
				aspectRatio = '2/1';
			}

			const tileSettings = {
				aspectRatio,
				width,
				height,
				x,
				y,
				isDefaultCell,
				tile
			};
			currTileIdx.current += 1;
			return tileSettings;
		});
	};

	const renderGrid = (cells: Cell[]) => cells.map((tile, idx) => (
        <div key={idx} style={
                !tile.isDefaultCell
                    ? { gridRow: `${tile.y + 1} / span ${tile.height}`, gridColumnEnd: `${tile.width} span` }
                    : {}
            }
        >
            {tile.tile}
        </div>
    ));

	return (
		<>
			{children.map((slide: ReactElement, idx: number) => {
				const cells: Cell[] = generateCells(slide?.props.children);
				const gridStyles = { gridTemplateColumns: `repeat(${slideLength}, 1fr)` };
				const { current: gridElement } = gridContainer;

                if (gridElement) {
					const gap: number = parseFloat(getComputedStyle(gridElement).gap);
					const rowHeight: number = (gridElement.offsetWidth - gap * (slideLength - 1)) / slideLength;

					if (cells[0]?.isDefaultCell) {
						Object.assign(gridStyles, {
							gridTemplateRows: `repeat(auto-fit, ${rowHeight}px)`,
							gridAutoFlow: 'column dense'
						});
					}

					Object.assign(gridStyles, { gridAutoRows: `minmax(${rowHeight}px, 1fr)` });
				}

				return (
					<div
						key={slide?.key}
						aria-hidden={isAllItemsVisible ? 'false' : idx !== activeSlide}
						className={`${slideClsName} ${fading && idx === activeSlide ? fadingActive : ''}`}
						ref={idx === 0 ? slideRef : null}
					>
						<div ref={gridContainer} className={`games-layouts--container ${styles.grid}`} style={gridStyles}>
							{renderGrid(cells)}
						</div>
					</div>
				);
			})}
		</>
	);
};

export default LayoutTilesView;
